/**
 * Scoring Configuration — Single Source of Truth
 *
 * This file defines ALL scoring-related configuration for the platform:
 * - Score thresholds (breakpoints between 5 performance bands)
 * - Score colors (hex values for each status band)
 * - Metric weights (relative importance of each tracked metric)
 * - Helper functions (convert scores to statuses and colors)
 *
 * CRITICAL: This is the ONLY place these values should be defined.
 * All consumers import from here — no hardcoded thresholds or colors elsewhere.
 *
 * ARCHITECTURE: Imported by both backend (Node.js ESM) and frontend (Vite/React).
 * Keep this file pure JS with no platform-specific imports.
 *
 * Change History:
 * - 2025-11-05: Thresholds changed from 85/70/50 to 90/75/60
 * - 2026-03-08: Centralized colors, weights, and helpers into this file
 * - 2026-03-08: 5-band system: excellent(95)/good(85)/fair(70)/atRisk(50)/needsSupport
 *
 * Future Enhancement:
 * - Values could be loaded from database via admin UI
 * - Consumers using helper functions (getScoreColor, etc.) would need zero changes
 */

// --- Score Band Definitions (Coaching Methodology) ---
//
// Excellent (A) — 95-100
//   Crushing it. Hitting all measured targets. Near-perfect adherence across
//   every scorable metric. This is the aspirational ceiling, not the expectation.
//
// Good (B) — 85-94
//   Doing very well. Everything trending in the right direction. Improvements
//   to reach Excellent would mean dialing in to the highest caliber across ALL
//   metrics. This is a perfectly acceptable — and preferred — operating range.
//   If you're in Good, you're doing everything right without being the overachiever.
//
// Fair (C) — 70-84
//   The "C average student." Not failing, not excelling. Meeting the spirit of
//   the program but struggling with one larger (or a couple smaller) goals that
//   keep the score down. Targeted coaching or insight into specific habits could
//   easily drive into Good. Expected landing zone for most clients in their early
//   career with us — as they "get it," they trend Fair → Good.
//
// At Risk (D) — 50-69
//   The struggling client. One or more goals they can't meet consistently,
//   reflected in daily scores. Where Fair is the client hitting carbs/calories
//   but struggling with sodium and sleep, At Risk is having issues across the
//   board — barely holding it together. Prioritize opportunities, pick battles,
//   find low-hanging fruit. Goal: move At Risk → Fair through incremental wins.
//
// Needs Support (F) — below 50
//   The figurative train wreck. Not tracking well, not adhering to standards.
//   Scores wildly out of spec on both sides. So many metrics scoring so poorly
//   that everything drags down. Requires deliberate work to identify root causes
//   that may go beyond "stop eating crap" — mental, emotional, or other big-picture
//   factors expressing as poor eating and wellness habits.
//
// no-data — score is exactly 0 (no metrics recorded for the day)

// --- Score Thresholds ---
// 5-band scoring system. To modify bands:
// 1. Change values here
// 2. Update color in SCORE_COLORS if adding/removing a band
// 3. Update the three helper functions below

const SCORE_THRESHOLDS = {
  excellent: 95,  // Exceptional — near-perfect adherence
  good:      85,  // Solid — consistently on target
  fair:      70,  // Okay — room for improvement
  atRisk:    50,  // Struggling — significant deviation
  // Below 50 = "needsSupport"
  // Exactly 0 = "no-data"
};

// --- Score Colors ---
// Co-located with thresholds so a band change is a single-file edit.

const SCORE_COLORS = {
  excellent: '#15803d',  // Tailwind green-700 (dark forest green)
  good:      '#84cc16',  // Tailwind lime-500 (lime green)
  fair:      '#facc15',  // Tailwind yellow-400 (lemon yellow)
  atRisk:    '#f97316',  // Tailwind orange-500 (pumpkin orange)
  needsSupport: '#ef4444',  // Tailwind red-400 (red)
  noData:    '#6b7280',  // Tailwind gray-500
};

// --- Metric Weights ---
// Used by calculateDailyScore, calculateSoftScore, calculateRealScore.
// Higher weight = more impact on final score. Self-normalizing: only
// active-goal weights contribute to the denominator.
// Updated 2025-11-01: Rebalanced to prioritize nutrition over activity/wellness.

const METRIC_WEIGHTS = {
  // Tier 1: Nutrition Foundation
  calories:     40,  // Primary focus (was 20)

  // Tier 2: Macronutrients
  protein_g:    30,  // Critical macro (was 10)
  carbs_g:      30,  // Critical macro (was 10)
  fats_g:       30,  // Critical macro (was 10)

  // Tier 3: Micronutrients
  sodium_mg:    20,  // Important micro (was 15)
  potassium_mg: 20,  // Pairs with sodium (NEW)
  fiber_g:      20,  // Important micro (was 5)

  // Tier 4: Activity
  steps:        10,  // Movement tracking (was 25)

  // Tier 5: Wellness
  water_intake: 10,  // Hydration - unit-agnostic (was 15)
  sleep_hours:  10,  // Rest (was 25)

  // Tier 6: Vibe Metrics
  energy_level:  5,  // Subjective wellness (was 15)
  stress_level:  5,  // Subjective wellness (was 10)

  // Excluded from scoring
  weight:        0,  // Result metric, not target (was 20)
  oil_tbsp:      5,  // Legacy metric
};

// --- Helper Functions ---
// These are the stable API surface. All consumers should prefer these
// over reading SCORE_THRESHOLDS directly. When thresholds move to a
// database, only this file changes — callers using helpers need no updates.

/**
 * Convert a numeric health score to a status string.
 * @param {number} score - Health score (0-100)
 * @returns {'excellent' | 'good' | 'fair' | 'atRisk' | 'needsSupport' | 'no-data'}
 */
function getScoreStatus(score) {
  if (score >= SCORE_THRESHOLDS.excellent) return 'excellent';
  if (score >= SCORE_THRESHOLDS.good)      return 'good';
  if (score >= SCORE_THRESHOLDS.fair)      return 'fair';
  if (score >= SCORE_THRESHOLDS.atRisk)    return 'atRisk';
  if (score > 0)                           return 'needsSupport';
  return 'no-data';
}

/**
 * Convert a numeric health score directly to a hex color.
 * @param {number} score - Health score (0-100)
 * @returns {string} Hex color string
 */
function getScoreColor(score) {
  if (score >= SCORE_THRESHOLDS.excellent) return SCORE_COLORS.excellent;
  if (score >= SCORE_THRESHOLDS.good)      return SCORE_COLORS.good;
  if (score >= SCORE_THRESHOLDS.fair)      return SCORE_COLORS.fair;
  if (score >= SCORE_THRESHOLDS.atRisk)    return SCORE_COLORS.atRisk;
  if (score > 0)                           return SCORE_COLORS.needsSupport;
  return SCORE_COLORS.noData;
}

/**
 * Convert a status string to a hex color.
 * @param {string} status - Status string from getScoreStatus()
 * @returns {string} Hex color string
 */
function getStatusColor(status) {
  return SCORE_COLORS[status] || SCORE_COLORS.noData;
}

export {
  SCORE_THRESHOLDS,
  SCORE_COLORS,
  METRIC_WEIGHTS,
  getScoreStatus,
  getScoreColor,
  getStatusColor,
};
